import { Box, Chip, Paper, Skeleton, Stack, Typography } from '@mui/material';
import { useForm } from 'react-hook-form';
import { IoCalendarClearOutline } from 'react-icons/io5';
import { RxCross2 } from 'react-icons/rx';
import AppliedCouponIcon from 'assets/icons/AppliedCouponIcon';
import Input from 'components/Common/Input';
import { CacheKeys } from '@/utils/cacheKeys';
import { formatDollars } from '@/utils/helperFunctions';
import Button from '@/components/Common/Button';
import { useAddAgencyCoupon, useRemoveAgencyCoupon } from '@/apis/agency/billing/hook';
import WarningIcon from '@/assets/icons/WarningIcon';

const ApplyCoupon = ({ appliedCouponDetails, isLoading }: any) => {
  let businessIds: number[];
  if (localStorage.getItem(CacheKeys.businessIds)) {
    businessIds = JSON.parse(localStorage.getItem(CacheKeys.businessIds) ?? '');
  }
  const { mutate: addCoupon, isPending: addingCoupon } = useAddAgencyCoupon();
  const { mutate: removeCoupon, isPending: removingCoupon } = useRemoveAgencyCoupon();
  const { handleSubmit, control } = useForm();
  const isExpired = !appliedCouponDetails?.is_active;

  const couponTheme = isExpired
    ? {
        border: '#D32F2F',
        bg: '#FFF4F4',
        text: '#D32F2F',
        subText: '#DA0808',
        chipBg: '#FDEAEA',
        chipBorder: '#DA0808',
        icon: <WarningIcon />
      }
    : {
        border: '#077F13',
        bg: '#F4FFF5',
        text: '#2E7D32',
        subText: '#2E7D32',
        chipBg: '#DBFCDE',
        chipBorder: '#077F13',
        icon: <AppliedCouponIcon />
      };

  const onSubmit = async (data: any) => {
    data = { ...data, business_id: businessIds?.[0] };
    await addCoupon(data);
  };

  if (isLoading) return <ApplyCouponSectionSkeleton />;

  return (
    <Paper variant='outlined' sx={{ p: { xs: 1.5, lg: 2 }, mb: 3, borderRadius: 2 }}>
      <Stack spacing={2}>
        {/* Static Header */}
        <Box>
          <Typography variant='subtitle1' fontWeight={500} sx={{ fontSize: { xs: '1.125rem', lg: 20 } }}>
            Apply Coupon
          </Typography>
          <Typography variant='body2' color='text.secondary' sx={{ fontSize: { xs: '14px', lg: 16 } }} mt={0.5}>
            Enter a coupon code to get discounts on your subscription
          </Typography>
        </Box>

        {/* Conditional Rendering */}
        {appliedCouponDetails?.code ? (
          <Box
            sx={{
              border: `1px solid ${couponTheme.border}`,
              borderRadius: 1,
              bgcolor: couponTheme.bg,
              p: { xs: 1.5, lg: 2 },
              display: 'flex',
              flexDirection: { xs: 'column', lg: 'row' },
              alignItems: { xs: 'flex-start', lg: 'center' },
              justifyContent: 'space-between',
              gap: { xs: 2, lg: 0 },
              width: '100%'
            }}
          >
            {/* Left Section */}
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={{ xs: 1.5, sm: 2 }}
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              sx={{ flex: 1, width: '100%' }}
            >
              {couponTheme.icon}
              <Stack spacing={0.5} sx={{ width: { xs: '100%', sm: 'auto' } }}>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  alignItems={{ xs: 'flex-start', sm: 'center' }}
                  spacing={1}
                  flexWrap='wrap'
                >
                  <Typography sx={{ fontSize: { xs: '1rem', lg: 20 }, fontWeight: 500, color: couponTheme.chipBorder }}>
                    {appliedCouponDetails.name}
                  </Typography>
                  <Chip
                    label={
                      appliedCouponDetails.discount_type === 'percentage'
                        ? appliedCouponDetails.total_amount != null && Number(appliedCouponDetails.total_amount) > 0
                          ? `${appliedCouponDetails.discount_value}% Off up to ${formatDollars(appliedCouponDetails.total_amount)}`
                          : `${appliedCouponDetails.discount_value}% Off`
                        : `${formatDollars(appliedCouponDetails.discount_value)} Off`
                    }
                    size='small'
                    sx={{
                      bgcolor: couponTheme.chipBg,
                      borderRadius: '34px',
                      border: `1px solid ${couponTheme.border}`,
                      color: couponTheme.subText,
                      fontWeight: 500,
                      height: 24,
                      fontSize: { xs: '0.7rem', lg: '0.75rem' }
                    }}
                  />
                </Stack>

                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={{ xs: 1, sm: 3 }}
                  alignItems={{ xs: 'flex-start', sm: 'center' }}
                  flexWrap='wrap'
                >
                  <Typography
                    variant='body2'
                    sx={{ color: couponTheme.subText, fontSize: { xs: '12px', lg: '0.875rem', fontWeight: '400px' } }}
                  >
                    Code: <strong>{appliedCouponDetails.code}</strong>
                  </Typography>

                  <Stack direction='row' spacing={0.5} alignItems='center'>
                    <IoCalendarClearOutline color={couponTheme.subText} size={14} />
                    <Typography
                      variant='body2'
                      fontWeight={500}
                      sx={{ color: couponTheme.subText, fontSize: { xs: '12px', lg: '0.875rem' } }}
                    >
                      Applied: {appliedCouponDetails.applied_on}
                    </Typography>
                  </Stack>

                  <Stack direction='row' spacing={0.5} alignItems='center'>
                    <IoCalendarClearOutline color={couponTheme.subText} size={14} />
                    <Typography
                      variant='body2'
                      fontWeight={500}
                      sx={{ color: couponTheme.subText, fontSize: { xs: '12px', lg: '0.875rem' } }}
                    >
                      Valid Until: {appliedCouponDetails.valid_until}
                    </Typography>
                  </Stack>
                </Stack>
              </Stack>
            </Stack>

            {/* Right Remove Button */}
            <Button
              variant='outlined'
              size='small'
              disabled={removingCoupon}
              startIcon={<RxCross2 />}
              onClick={() => removeCoupon({ business_id: businessIds?.[0], code: appliedCouponDetails?.code })}
              sx={{
                borderColor: '#E0E0E0',
                color: '#6E6E6E',
                borderRadius: 8,
                px: 2,
                textTransform: 'none',
                bgcolor: 'white',
                width: { xs: '100%', lg: 'auto' },
                '&:hover': { bgcolor: '#f5f5f5', borderColor: '#ccc' }
              }}
            >
              Remove
            </Button>
          </Box>
        ) : (
          <Stack
            direction={{ xs: 'column', lg: 'row' }}
            gap={{ xs: 2, lg: 4 }}
            justifyContent='space-between'
            alignItems={{ xs: 'stretch', lg: 'center' }}
            component='form'
            onSubmit={handleSubmit(onSubmit)}
          >
            <Box sx={{ width: { xs: '100%', lg: 'auto' }, flex: { xs: 'none', lg: 1 } }}>
              <Input name='code' control={control} label='Coupon Code' fullWidth />
            </Box>
            <Box m={{ xs: 0, lg: 'auto' }} sx={{ width: { xs: '100%', lg: 'auto' } }}>
              <Button
                variant='contained'
                type='submit'
                size='small'
                isPending={addingCoupon}
                fullWidth
                sx={{
                  borderRadius: 5,
                  whiteSpace: 'nowrap',
                  width: { xs: '100%', lg: 'auto' },
                  minWidth: { lg: 'auto' }
                }}
              >
                Apply Coupon
              </Button>
            </Box>
          </Stack>
        )}
      </Stack>
    </Paper>
  );
};

export default ApplyCoupon;

// skelton

const ApplyCouponSectionSkeleton = () => {
  return (
    <Paper variant='outlined' sx={{ p: { xs: 1.5, lg: 2 }, mb: 3, borderRadius: 2 }}>
      <Stack spacing={2}>
        {/* Static Heading */}
        <Box>
          <Typography variant='subtitle1' fontWeight={500} sx={{ fontSize: { xs: '1.125rem', lg: 20 } }}>
            Apply Coupon
          </Typography>
          <Typography variant='body2' color='text.secondary' sx={{ fontSize: { xs: '14px', lg: 16 } }} mt={0.5}>
            Enter a coupon code to get discounts on your subscription
          </Typography>
        </Box>

        {/* Skeleton Card for Applied Coupon */}
        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            p: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%'
          }}
        >
          {/* Left Section */}
          <Stack direction='row' spacing={2} alignItems='center' width='100%'>
            <Skeleton variant='circular' width={36} height={36} />
            <Stack spacing={1} width='100%'>
              <Skeleton variant='text' width='40%' height={24} />
              <Stack direction='row' spacing={2} alignItems='center'>
                <Skeleton variant='text' width='30%' height={20} />
                <Skeleton variant='text' width='30%' height={20} />
                <Skeleton variant='text' width='30%' height={20} />
              </Stack>
            </Stack>
          </Stack>

          {/* Right Remove Button */}
          <Skeleton variant='rounded' width={90} height={36} />
        </Box>
      </Stack>
    </Paper>
  );
};
